import { formatDistanceToNow } from "@plasma/utils/time";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import {
  Activity,
  AlertCircle,
  Badge as BadgeIcon,
  Clock,
  MapPin,
  ShieldAlert,
  Zap,
} from "lucide-react";

import { billingGetCurrentBillingInfo, billingGetTierInfo } from "~/api-refs";
import { PageWrapper } from "~/components/page-wrapper";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { Spinner } from "~/components/ui/spinner";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";

const TIER_LABELS: Record<string, string> = {
  free: "Free",
  growth: "Growth",
  enterprise: "Enterprise",
};

const TIER_PROCESSING_RATES: Record<string, number> = {
  free: 4.0,
  growth: 3.0,
  enterprise: 2.5,
};

export const Route = createFileRoute("/_authenticated/settings/usage")({
  component: UsagePage,
  pendingComponent: () => (
    <PageWrapper
      description="View your current tier and active sessions"
      title="Usage"
    >
      <div className="flex items-center justify-center p-12">
        <Spinner className="size-8" />
      </div>
    </PageWrapper>
  ),
});

function UsagePage() {
  const {
    hasPermission,
    isLoading: permissionsLoading,
    hasEmployee,
  } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  const tierInfo = useQuery(
    billingGetTierInfo,
    canViewIntegrations ? {} : "skip"
  );
  const billingInfo = useQuery(
    billingGetCurrentBillingInfo,
    canViewIntegrations ? {} : "skip"
  );

  if (permissionsLoading) {
    return (
      <PageWrapper
        description="View your current tier and active sessions"
        title="Usage"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  if (!hasEmployee) {
    return (
      <PageWrapper
        description="View your current tier and active sessions"
        title="Usage"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  if (!canViewIntegrations) {
    return (
      <PageWrapper
        description="View your current tier and active sessions"
        title="Usage"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view usage information. Contact your
            manager to request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  if (!(tierInfo && billingInfo)) {
    return (
      <PageWrapper
        description="View your current tier and active sessions"
        title="Usage"
      >
        <div className="flex items-center justify-center p-12">
          <Spinner className="size-8" />
        </div>
      </PageWrapper>
    );
  }

  const currentTier = tierInfo.tier ?? "free";
  const processingRate = TIER_PROCESSING_RATES[currentTier] ?? 4.0;

  return (
    <PageWrapper
      description="View your current tier and active sessions"
      title="Usage"
    >
      <div className="space-y-6">
        {/* Tier Summary */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BadgeIcon className="size-5" />
                  Plan Summary
                </CardTitle>
                <CardDescription>
                  Your current tier and billing details
                </CardDescription>
              </div>
              <Badge
                className={currentTier === "free" ? "" : "bg-success"}
                variant={currentTier === "free" ? "secondary" : "default"}
              >
                {TIER_LABELS[currentTier] ?? currentTier}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="bg-card rounded-lg border p-4 text-center">
                <p className="text-muted-foreground mb-1 text-sm">
                  Processing Rate
                </p>
                <p className="text-3xl font-bold">{processingRate}%</p>
                <p className="text-muted-foreground text-xs">per transaction</p>
              </div>
              <div className="bg-card rounded-lg border p-4 text-center">
                <p className="text-muted-foreground mb-1 text-sm">Locations</p>
                <p className="flex items-center justify-center gap-1 text-3xl font-bold">
                  <MapPin className="text-primary size-5" />
                  {tierInfo.locationCount}
                </p>
                <p className="text-muted-foreground text-xs">
                  restaurants (auto-managed)
                </p>
              </div>
              <div className="bg-card rounded-lg border p-4 text-center">
                <p className="text-muted-foreground mb-1 text-sm">Active Now</p>
                <p className="text-success text-3xl font-bold">
                  {billingInfo.activeSessionCount}
                </p>
                <p className="text-muted-foreground text-xs">devices online</p>
              </div>
            </div>

            <div className="bg-muted mt-4 rounded-lg p-4">
              <p className="text-muted-foreground text-sm">
                Location count updates automatically when you add or remove
                restaurants. Billing is based on your tier and number of
                locations.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Active Sessions (Analytics) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="size-5" />
              Active Sessions
            </CardTitle>
            <CardDescription>
              POS devices currently online (analytics)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="bg-card flex items-center justify-between rounded-lg border p-6">
                <div className="space-y-1">
                  <p className="body-sm text-muted-foreground">
                    Active Sessions
                  </p>
                  <p className="text-4xl font-bold">
                    {billingInfo.activeSessionCount}
                  </p>
                </div>
                <Zap className="text-primary size-12 opacity-20" />
              </div>

              {billingInfo.signedInSessions.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="subtitle-sm">Active Devices</h4>
                  <div className="divide-y rounded-lg border">
                    {billingInfo.signedInSessions.map(
                      (session: {
                        id: string;
                        deviceName?: string;
                        startedAt: number;
                        lastHeartbeat: number;
                      }) => (
                        <div
                          className="hover:bg-muted/50 flex items-center justify-between p-4"
                          key={session.id}
                        >
                          <div className="space-y-1">
                            <p className="subtitle-sm">
                              {session.deviceName ?? "POS Device"}
                            </p>
                            <p className="body-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="size-3" />
                              Started {formatDistanceToNow(session.startedAt)}
                            </p>
                          </div>
                          <Badge
                            className="border-success text-success"
                            variant="outline"
                          >
                            <div className="bg-success mr-2 size-2 animate-pulse rounded-full" />
                            Active
                          </Badge>
                        </div>
                      )
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-8 text-center">
                  <Activity className="text-muted-foreground mx-auto mb-3 size-12 opacity-50" />
                  <h4 className="subtitle-sm mb-1">No Active Sessions</h4>
                  <p className="body-sm text-muted-foreground">
                    You currently have no active POS sessions. Start a session
                    on your POS device to begin.
                  </p>
                </div>
              )}

              <Alert>
                <AlertCircle className="size-4" />
                <AlertTitle>How Billing Works</AlertTitle>
                <AlertDescription>
                  <ul className="mt-2 list-inside list-disc space-y-1 text-sm">
                    <li>
                      Billing is based on your tier and number of restaurant
                      locations
                    </li>
                    <li>
                      Free tier: no monthly cost, {TIER_PROCESSING_RATES.free}%
                      processing fee
                    </li>
                    <li>
                      Paid tiers: lower processing fees with per-location
                      monthly pricing
                    </li>
                    <li>
                      Location count updates automatically when restaurants are
                      added or removed
                    </li>
                    <li>
                      Device sessions are tracked for analytics only and do not
                      affect billing
                    </li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  );
}
